import { useCallback, useState } from 'react';
import {
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { arabicFontStack } from '../tokens';

/**
 * Data-residency consent — the shared FoloLabs disclosure + consent control.
 *
 * One source of truth for the cross-border data notice used by every
 * product. Split into a hook (state/persistence, owned by the consumer)
 * and a presentational component (two layouts):
 *   - `variant="gate"`  — full-screen gate (the launcher's last gate before
 *      real data; user must accept to continue).
 *   - `variant="banner"` — inline notice for product pages; pair it with
 *      `useDataResidencyConsent()` to BLOCK upload (and any data-writing
 *      action) until the box is ticked.
 *
 * Usage (product, gating upload):
 *   const { consented, accept } = useDataResidencyConsent(userEmail);
 *   <DataResidencyConsent variant="banner" onAccept={accept} />
 *   <Button disabled={!consented}>Upload</Button>
 *
 * Body copy is a PLACEHOLDER pending final CFO/legal wording — change it in
 * ONE place here and every product updates. Bump CONSENT_VERSION when the
 * text changes materially so users re-consent.
 */

export const CONSENT_VERSION = 'v2';

function storageKey(userKey: string): string {
  const id = userKey.trim().toLowerCase() || 'anonymous';
  return `fololabs.consent.${CONSENT_VERSION}.${id}`;
}

/**
 * Consent state for a signed-in user (key by email / sub). Products gate
 * upload on `consented`; wire `accept` to the consent control's `onAccept`.
 */
export function useDataResidencyConsent(userKey = 'anonymous') {
  const [consented, setConsented] = useState(() => {
    try {
      return localStorage.getItem(storageKey(userKey)) != null;
    } catch {
      return false;
    }
  });
  const accept = useCallback(() => {
    try {
      localStorage.setItem(storageKey(userKey), 'accepted');
    } catch {
      /* non-fatal — proceed for this session even if we can't persist */
    }
    setConsented(true);
  }, [userKey]);
  return { consented, accept };
}

type Lang = 'en' | 'ar';

const COPY: Record<
  Lang,
  {
    dir: 'ltr' | 'rtl';
    heading: string;
    placeholderTag: string;
    body: string[];
    consent: string;
    continueLabel: string;
    switchLabel: string;
  }
> = {
  en: {
    dir: 'ltr',
    heading: 'Before you continue',
    placeholderTag: 'PLACEHOLDER — pending final CFO / legal wording',
    body: [
      'FoloLabs processes and stores your data on infrastructure located outside the Kingdom of Saudi Arabia (currently the United States, AWS us-east-1).',
      'By continuing — and before uploading any data — you acknowledge this cross-border transfer and storage, and consent to it for the purpose of using FoloLabs products.',
    ],
    consent:
      'I understand my data is stored outside Saudi Arabia and I consent to this transfer.',
    continueLabel: 'Continue',
    switchLabel: 'العربية',
  },
  ar: {
    dir: 'rtl',
    heading: 'قبل المتابعة',
    placeholderTag: 'نص مؤقت — في انتظار الصياغة النهائية من الإدارة المالية / القانونية',
    body: [
      'تقوم FoloLabs بمعالجة وتخزين بياناتك على بنية تحتية تقع خارج المملكة العربية السعودية (حاليًا الولايات المتحدة، AWS us-east-1).',
      'بالمتابعة — وقبل رفع أي بيانات — فإنك تقر بنقل وتخزين البيانات عبر الحدود وتوافق على ذلك لغرض استخدام منتجات FoloLabs.',
    ],
    consent: 'أفهم أن بياناتي مخزّنة خارج المملكة العربية السعودية وأوافق على هذا النقل.',
    continueLabel: 'متابعة',
    switchLabel: 'English',
  },
};

export interface DataResidencyConsentProps {
  /** Full-screen gate (launcher) or inline product banner (gates upload). */
  variant?: 'gate' | 'banner';
  /** Called when the user consents. Wire to `useDataResidencyConsent().accept`. */
  onAccept?: () => void;
}

export function DataResidencyConsent({
  variant = 'gate',
  onAccept,
}: DataResidencyConsentProps) {
  const [lang, setLang] = useState<Lang>('en');
  const [checked, setChecked] = useState(false);
  const t = COPY[lang];
  const isAr = lang === 'ar';

  const switcher = (
    <Button
      variant="subtle"
      size="xs"
      color="aquifer"
      onClick={() => setLang(isAr ? 'en' : 'ar')}
    >
      {t.switchLabel}
    </Button>
  );

  const placeholderTag = (
    <Text
      size="xs"
      c="warning.7"
      fw={600}
      style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
    >
      {t.placeholderTag}
    </Text>
  );

  if (variant === 'banner') {
    // Ticking the box IS the consent — there's no separate button inline.
    return (
      <Card
        withBorder
        radius="md"
        p="md"
        dir={t.dir}
        style={{
          fontFamily: isAr ? arabicFontStack : undefined,
          backgroundColor: 'var(--mantine-color-mist-1)',
          borderColor: 'var(--mantine-color-warning-3)',
        }}
      >
        <Stack gap="xs">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text fw={600} c="aquifer.7" size="sm">
              {t.heading}
            </Text>
            {switcher}
          </Group>
          {placeholderTag}
          <Text size="sm" c="slate.8" lh={1.55}>
            {t.body[0]}
          </Text>
          <Checkbox
            checked={checked}
            onChange={(e) => {
              const v = e.currentTarget.checked;
              setChecked(v);
              if (v) onAccept?.();
            }}
            color="aquifer"
            label={
              <Text size="sm" c="slate.8">
                {t.consent}
              </Text>
            }
          />
        </Stack>
      </Card>
    );
  }

  // gate — full screen, Continue confirms.
  return (
    <Center
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--mantine-color-mist-2)',
        padding: '1rem',
      }}
    >
      <Stack align="center" gap="lg" style={{ width: '100%', maxWidth: 460 }}>
        <Card
          withBorder
          radius="md"
          p="xl"
          shadow="sm"
          w="100%"
          dir={t.dir}
          style={{ fontFamily: isAr ? arabicFontStack : undefined }}
        >
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Title order={3} c="aquifer.7">
                {t.heading}
              </Title>
              {switcher}
            </Group>
            {placeholderTag}
            <Stack gap="sm">
              {t.body.map((p, i) => (
                <Text key={i} size="sm" c="slate.8" lh={1.6}>
                  {p}
                </Text>
              ))}
            </Stack>
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.currentTarget.checked)}
              color="aquifer"
              label={
                <Text size="sm" c="slate.8">
                  {t.consent}
                </Text>
              }
            />
            <Button
              color="aquifer"
              size="md"
              radius="md"
              fullWidth
              disabled={!checked}
              onClick={() => onAccept?.()}
            >
              {t.continueLabel}
            </Button>
          </Stack>
        </Card>
      </Stack>
    </Center>
  );
}
